import React, { useEffect, useMemo, useState } from 'react';
import { pr as api } from '../services/api';
import FiltresPanel from './FiltresPanel';
import { useFiltresVentes, usePanneau, basculer } from './ventesUtils';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './VentesJour.css';  // rg-* du regroupement
import './Ventes.css';
import './Stock.css';
import './PrStock.css';
import './Creances.css';   // cr-btn

// ─── PR — Évolution du stock (2026-10-29) ───────────────────────────────────
// Onglet dédié (demande du user), comme VN / VO : la valeur du stock pièces à
// chaque fin de mois (depuis décembre 2025) puis au jour de l'export, par
// concession, marque, famille ou dépôt ; les plus fortes hausses et baisses
// par référence ; la série d'une référence cherchée. Pas de « périmètre
// constant » (user : la base sera complétée) — les sociétés Métropole entrées
// en avril 2026 apparaissent en avril.

const nf = new Intl.NumberFormat('fr-FR');
const cM = (v) => {
  v = Math.round(v || 0); const a = Math.abs(v);
  if (a >= 1e6) return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v / 1e6) + ' M€';
  if (a >= 1e3) return nf.format(Math.round(v / 1e3)) + ' k€';
  return nf.format(v) + ' €';
};
const k = (v) => (v == null ? '—' : nf.format(Math.round(v / 1000)));
const ks = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${nf.format(Math.round(v / 1000))}`);
const eur = (v) => (v == null ? '—' : `${nf.format(Math.round(v))} €`);
const eurS = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${nf.format(Math.round(v))} €`);
const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const moisCourt = (ym) => `${MOIS[Number(ym.slice(5)) - 1]} ${ym.slice(2, 4)}`;
const AXES = [{ c: 'societe', l: 'Concession' }, { c: 'fournisseur', l: 'Marque' }, { c: 'famille', l: 'Famille' }, { c: 'depot', l: 'Dépôt' }];
const v0 = () => ({ pa: 0, pump: 0 });
// Variations par référence (2026-10-29) : quatre catégories distinctes.
const TYPES = [
  { c: 'hausses', l: 'Plus fortes hausses', titre: 'Références présentes aux deux dates dont la valeur a le plus augmenté' },
  { c: 'baisses', l: 'Plus fortes baisses', titre: 'Références présentes aux deux dates dont la valeur a le plus diminué' },
  { c: 'nouvelles', l: 'Nouvelles références', titre: 'Absentes du stock à la première fin de mois, présentes au jour (par valeur)' },
  { c: 'sorties', l: 'Sorties du stock', titre: 'Présentes à la première fin de mois, absentes au jour (par valeur de départ)' },
];

export default function PrStockEvolution({ branche = 'auto-antilles' }) {
  const [data, setData] = useState(null);
  const [vari, setVari] = useState(null);
  const [serie, setSerie] = useState(null);
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState('');
  const [tout, setTout] = useState(false);
  const [toutesVar, setToutesVar] = useState(false);
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const val = f.params.get('val') === 'pump' ? 'pump' : 'pa';
  const axe = AXES.some((a) => a.c === f.params.get('axe')) ? f.params.get('axe') : 'societe';
  const cle = axe !== 'societe' ? (f.params.get('cle') || '') : '';
  const ref = f.params.get('ref') || '';
  const typeVar = TYPES.some((t) => t.c === f.params.get('var')) ? f.params.get('var') : 'hausses';

  useEffect(() => {
    let actif = true; setErreur('');
    api.stockEvolution({ branche, societes: f.societesUrl || undefined, axe: axe === 'societe' ? undefined : axe })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche, f.societesUrl, axe]);
  useEffect(() => {
    let actif = true; setVari(null);
    api.stockVariations({ branche, societes: f.societesUrl || undefined, axe: cle ? axe : undefined, cle: cle || undefined, val: val === 'pump' ? 'pump' : undefined,
      type: typeVar === 'hausses' ? undefined : typeVar })
      .then((r) => { if (actif) setVari(r.data); }).catch(() => { if (actif) setVari({ lignes: [], comptes: {} }); });
    return () => { actif = false; };
  }, [branche, f.societesUrl, axe, cle, val, typeVar]);
  useEffect(() => {
    let actif = true; setSerie(null); setSaisie(ref);
    if (!ref) return undefined;
    api.stockReference({ branche, societes: f.societesUrl || undefined, reference: ref })
      .then((r) => { if (actif) setSerie(r.data); }).catch(() => { if (actif) setSerie({ lignes: [], mois: [] }); });
    return () => { actif = false; };
  }, [branche, f.societesUrl, ref]);

  const feuilles = useMemo(() => ((data?.axe || 'societe') === 'societe' ? (data?.lignes || []).map((s) => ({ ...s, libelle: s.nom })) : []), [data]);
  const axesDispo = useMemo(() => axesDisponibles(feuilles), [feuilles]);
  const sommer = (membres) => {
    const nbm = (data?.mois || []).length;
    const t = { evolution: Array.from({ length: nbm }, () => null), jour: v0() };
    membres.forEach((m) => {
      m.evolution.forEach((e, i) => { if (e) { const c = t.evolution[i] || v0(); c.pa += e.pa; c.pump += e.pump; t.evolution[i] = c; } });
      t.jour.pa += m.jour.pa; t.jour.pump += m.jour.pump;
    });
    return t;
  };
  const maille = useRegroupement();
  const arbre = useMemo(() => (feuilles.length ? construireArbre(feuilles, maille.niveau, sommer, maille.ouverts, (x) => x.jour?.pa) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts]);

  const Panneau = (
    <FiltresPanel ouvert={ouvert} onToggle={toggle}
      societesDispo={societesDispo} societesActives={f.selected} onToggleSociete={f.toggleSociete}
      onToutesSocietes={() => f.setFiltre({ societes: '' })} onAucuneSociete={() => f.setFiltre({ societes: '__aucune__' })}
      couleurDe={f.couleurDe} nbActifs={(f.societesUrl ? 1 : 0) + (cle ? 1 : 0)} onReset={() => f.setFiltre({ societes: '', cle: '' })} />
  );
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;
  if (data.indisponible || data.vide) {
    return <><main className="vt-page"><div className="vt-head"><div><h1>PR — Évolution du stock</h1></div></div>
      <div className="bi-empty"><h2>{data.indisponible ? 'Stock pièces non importé' : 'Aucune société sélectionnée'}</h2>
        <p>{data.indisponible ? 'Les exports stock pièces ne sont pas encore ingérés dans A97 DATA.' : 'Cochez au moins une société dans les filtres à droite.'}</p></div></main>{Panneau}</>;
  }

  const V = (o) => (o ? o[val] : null);
  // ⚠️ Pendant le rechargement d'un changement d'axe, `data` est encore celui
  // de l'axe précédent (2026-10-29 : lignes de sociétés rendues comme des
  // marques, sans libellé ni clé, que React laissait à l'écran à côté des
  // nouvelles). On affiche donc selon l'axe des DONNÉES, et on estompe.
  const axeD = data.axe || 'societe';
  const enCours = axeD !== axe;
  const mois = data.mois; const dernier = mois.length - 1;
  const dateStock = data.dateStock ? new Date(data.dateStock).toLocaleDateString('fr-FR') : '';
  const lignesAxe = axeD === 'societe' ? arbre : (tout ? data.lignes : data.lignes.slice(0, 25));
  const tot = sommer(axeD === 'societe' ? feuilles : data.lignes);
  const AX = AXES.find((a) => a.c === axeD);
  const premier = tot.evolution.findIndex(Boolean);
  const cellules = (l) => {
    const e = l.evolution; const j = V(l.jour);
    const d1 = dernier >= 0 && e[dernier] ? j - V(e[dernier]) : null;
    const i0 = e.findIndex(Boolean); const d0 = i0 >= 0 ? j - V(e[i0]) : null;
    return <>
      {e.map((x, i) => <td key={i}>{x ? k(V(x)) : '—'}</td>)}
      <td><b>{k(j)}</b></td><td>{ks(d1)}</td><td>{ks(d0)}</td>
    </>;
  };
  const ligneSoc = (s) => {
    const cible = s.feuille ? s.code : (s.membres || []).join(',');
    return (
      <tr key={s.cle} className={`tbl-clic rg-n${s.niveau}${f.societesUrl === cible ? ' actif' : ''}`}
        onClick={() => f.setFiltre({ societes: basculer(f.societesUrl, cible) })}>
        <td className="l">
          <span className="rg-in" style={{ width: s.niveau * 14 }} />
          {s.feuille ? <span className="rg-chev vide" /> : (
            <button type="button" className={`rg-btn${maille.ouverts.has(s.cle) ? ' on' : ''}`} aria-label="Déplier"
              title={maille.ouverts.has(s.cle) ? 'Replier' : 'Voir le détail par société'}
              onClick={(ev) => { ev.stopPropagation(); maille.basculer(s.cle); }}>▶</button>
          )}
          {s.libelle || s.nom}{!s.feuille && <span className="rg-nb">{s.nbEnfants}</span>}
        </td>
        {cellules(s)}
      </tr>
    );
  };
  const TableVar = ({ lignes }) => (
    <div className="ps-var">
      <table className="vt-tbl tbl-dense">
        <thead><tr><th className="l">Référence</th><th className="l">Désignation</th><th className="l">Marque</th><th>Début</th><th>Au {dateStock.slice(0, 5)}</th><th>Écart</th></tr></thead>
        <tbody>
          {(toutesVar ? lignes : lignes.slice(0, 15)).map((r) => (
            <tr key={r.reference} className={`tbl-clic${ref === r.reference ? ' actif' : ''}`} title="Voir l’évolution de cette référence" onClick={() => f.setFiltre({ ref: r.reference })}>
              <td className="l ps-ref">{r.reference}</td><td className="l ps-des" title={r.designation}>{r.designation}</td><td className="l">{r.fournisseur}</td>
              <td>{eur(r.debut)}</td><td>{eur(r.fin)}</td><td><b>{eurS(r.fin - r.debut)}</b></td>
            </tr>
          ))}
          {!lignes.length && <tr><td className="l" colSpan={6}>Aucune.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>PR — Évolution du stock</h1></div>
          <div className="ps-tete">
            <span className="st-seg" aria-label="Valorisation" title="Valorisation de toute la page">
              <button type="button" className={val === 'pa' ? 'on' : ''} onClick={() => f.setFiltre({ val: '' })}>Prix d’achat</button>
              <button type="button" className={val === 'pump' ? 'on' : ''} onClick={() => f.setFiltre({ val: 'pump' })}>PUMP borné</button>
            </span>
            <div className="vt-asof"><b>{mois.length ? `fin ${moisCourt(mois[0])} → ${dateStock}` : dateStock}</b><br /><span>valorisé {val === 'pump' ? 'au PUMP borné' : 'au prix d’achat'}</span></div>
          </div>
        </div>

        <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
          <div className="vt-kpi"><div className="l">Fin {premier >= 0 ? moisCourt(mois[premier]) : '—'}</div><div className="v">{cM(V(tot.evolution[premier]))}</div><div className="d"><small>première fin de mois importée</small></div></div>
          <div className="vt-kpi"><div className="l">Fin {dernier >= 0 ? moisCourt(mois[dernier]) : '—'}</div><div className="v">{cM(V(tot.evolution[dernier]))}</div><div className="d"><small>dernière fin de mois</small></div></div>
          <div className="vt-kpi"><div className="l">Au {dateStock}</div><div className="v">{cM(V(tot.jour))}</div><div className="d"><small>photo du jour</small></div></div>
          <div className="vt-kpi"><div className="l">Écart depuis fin {premier >= 0 ? moisCourt(mois[premier]) : '—'}</div>
            <div className="v">{premier >= 0 ? `${V(tot.jour) - V(tot.evolution[premier]) > 0 ? '+' : ''}${cM(V(tot.jour) - V(tot.evolution[premier]))}` : '—'}</div>
            <div className={`d${(data.entrees || []).length ? ' neg' : ''}`}><small>{(data.entrees || []).length
              ? `dont l’entrée de ${data.entrees.map((e) => `${e.n} société${e.n > 1 ? 's' : ''} en ${moisCourt(e.ym)}`).join(', ')} (sans fin de mois avant)`
              : 'même périmètre sur toute la période'}</small></div></div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Par {AX.l.toLowerCase()}</h2>
            <div className="st-tools">
              <span className="st-seg" aria-label="Axe">
                {AXES.map((a) => <button key={a.c} type="button" className={axe === a.c ? 'on' : ''} onClick={() => { setTout(false); f.setFiltre({ axe: a.c === 'societe' ? '' : a.c, cle: '' }); }}>{a.l}</button>)}
              </span>
              {axeD === 'societe' && <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axesDispo} />}
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <p className="hint">Valeur en k€ à chaque fin de mois, puis au jour de l’export · — : pas de position importée ce mois-là · {axeD === 'societe' ? 'un clic filtre la page' : 'un clic filtre les variations par référence'}.</p>
            <table className={`vt-tbl tbl-dense ps-evo${enCours ? ' ps-charge' : ''}`}>
              <thead><tr><th className="l">{AX.l}</th>{mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}<th>Au {dateStock.slice(0, 5)}</th>
                <th title={dernier >= 0 ? `Écart du jour de l’export face à la fin ${moisCourt(mois[dernier])}` : ''}>vs {dernier >= 0 ? MOIS[Number(mois[dernier].slice(5)) - 1] : ''}</th>
                <th title="Écart du jour de l’export face à la première fin de mois de la ligne">vs début</th></tr></thead>
              <tbody>
                {axeD === 'societe' ? lignesAxe.map(ligneSoc) : lignesAxe.map((l, i) => (
                  <tr key={`${axeD}:${l.cle ?? i}`} className={`tbl-clic${cle === l.cle ? ' actif' : ''}`} onClick={() => f.setFiltre({ cle: basculer(cle, l.cle) })}>
                    <td className="l">{l.cle}</td>{cellules(l)}
                  </tr>
                ))}
              </tbody>
              {(axeD !== 'societe' || feuilles.length > 1) && (
                <tfoot><tr><td className="l">Total{axeD !== 'societe' && data.lignes.length >= 300 ? ' (300 premières lignes)' : ''}</td>{cellules(tot)}</tr></tfoot>
              )}
            </table>
            {axeD !== 'societe' && data.lignes.length > 25 && (
              <button type="button" className="pl-lien ps-plus" onClick={() => setTout(!tout)}>{tout ? 'Afficher les 25 premières' : `Afficher les ${data.lignes.length} lignes`}</button>
            )}
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Variations par référence</h2>
            <div className="st-tools">
              {cle && <span className="ps-filtre">{AX.l} : <b>{cle}</b><button type="button" title="Retirer le filtre" onClick={() => f.setFiltre({ cle: '' })}>×</button></span>}
              <span className="st-seg" aria-label="Variations">
                {TYPES.map((t) => (
                  <button key={t.c} type="button" className={typeVar === t.c ? 'on' : ''} title={t.titre}
                    onClick={() => { setToutesVar(false); f.setFiltre({ var: t.c === 'hausses' ? '' : t.c }); }}>
                    {t.l}{vari?.comptes?.[t.c] && <span className="st-seg-nb">{nf.format(vari.comptes[t.c].n)}</span>}
                  </button>
                ))}
              </span>
            </div>
          </div>
          <div className="vt-card-b">
            {!vari ? <div className="vt-skel" /> : (<>
              <p className="hint">{TYPES.find((t) => t.c === typeVar).titre}{vari.comptes?.[typeVar] ? ` · ${nf.format(vari.comptes[typeVar].n)} références, ${eurS(vari.comptes[typeVar].ecart)} au total` : ''} · valeur au jour face à la première fin de mois de chaque société · un clic affiche la référence.</p>
              <TableVar lignes={vari.lignes} />
              {vari.lignes.length > 15 && (
                <button type="button" className="pl-lien ps-plus" onClick={() => setToutesVar(!toutesVar)}>{toutesVar ? 'Afficher les 15 premières' : `Afficher les ${vari.lignes.length} premières`}</button>
              )}
            </>)}
          </div>
        </div>

        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Une référence</h2>
            <form className="st-tools" onSubmit={(e) => { e.preventDefault(); f.setFiltre({ ref: saisie.trim() }); }}>
              <input className="ps-cherche" value={saisie} onChange={(e) => setSaisie(e.target.value)} placeholder="N° de référence" />
              <button type="submit" className="cr-btn">Afficher</button>
              {ref && <button type="button" className="cr-btn ghost" onClick={() => f.setFiltre({ ref: '' })}>Effacer</button>}
            </form>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            {!ref ? <p className="hint">Saisissez une référence, ou cliquez sur une ligne des variations.</p>
              : !serie ? <div className="vt-skel" />
                : !serie.lignes.length ? <p className="hint">Référence « {ref} » introuvable dans le stock de la sélection.</p> : (<>
                  <p className="hint"><b className="ps-ref">{serie.reference}</b> {serie.designation}{serie.fournisseur ? ` · ${serie.fournisseur}` : ''} — valeur en € et quantité, par société.</p>
                  <table className="vt-tbl tbl-dense">
                    <thead><tr><th className="l">Société</th>{serie.mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}<th>Au {dateStock.slice(0, 5)}</th></tr></thead>
                    <tbody>
                      {serie.lignes.map((l) => (
                        <tr key={l.societe}><td className="l">{l.societe}</td>
                          {l.evolution.map((x, i) => <td key={i}>{x ? <>{eur(V(x))}<br /><small>{nf.format(x.q)} pc</small></> : '—'}</td>)}
                          <td><b>{l.jour ? eur(V(l.jour)) : '—'}</b>{l.jour && <><br /><small>{nf.format(l.jour.q)} pc</small></>}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </>)}
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
